// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import React, { forwardRef } from 'react'
import { array, bool, func, object, string } from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { isPristine, SubmissionError } from 'redux-form'
import Header from './Header'
import { DataTable } from 'components'
import * as DBSets from './Sets'
import { preventFormSubmission } from 'helpers/reduxFormFunctions'
import { warning } from 'react-notification-system-redux'
import {
  parseAndExportResultsData,
  submittedFormIsPristine,
  triggerSearch,
} from './helpers'

import {
  TOO_MANY_RESULTS,
  UNCAUGHT_ERROR,
} from 'errors'

// NOTE - in previous versions of react and react-redux, forwarding refs
// was easier and more straight forward. I'm starting to think that
// this is now a bad design for current react. When refactoring this code,
// keep that in mind.

// NOTE - the prop 'paginate' tells this component that it should support pagination
// This means that this component does its part to support pagination.
// This does NOT dynamically add the pagination component.
// You must also render the <Pagination /> component  (./common/Pagination)
// within the specified form for there to be a corresponding UI component.
// At this level it's really about doing clerical work to manage the pagination's
// meta information through redux-form

// NOTE - if you want to add pagination, you must
// 1. pass paginate === true as a prop
// 2. add the <Pagination /> component somewhere to the render tree (probably as a child of the <Form />)
// 3. teach the fetchData function to properly handle the pagination object in form values
// 4. set up your clear handler to revert to the actual initialValues, and not just dispatch(clear(formName))

class SearchAndSelect extends React.Component {
  static displayName = 'SearchAndSelect'

  static propTypes = {
    closeModal: func,
    customImportHandler: func,
    dbSet: string.isRequired,
    dynamicColumns: array,
    formName: string.isRequired,
    formIsPristine: bool.isRequired,
    idRoot: string.isRequired,
    highlightRows: bool,
    includeFlags: array,
    initialValues: object.isRequired,
    label: string.isRequired,
    paginate: bool,
    queryBasis: array,
    renderOpenInNewTab: bool,
    rowClass: string,
    selectRow: func.isRequired,
    shouldRowUpdate: func,
    submittedFormIsPristine: func.isRequired,
    triggerSearch: func.isRequired,
    triggerSearchOnMount: bool,
    unitType: string,
    warning: func.isRequired,
  }

  // Form, columns, fetchData, sortData, getSortColByFormValues and prepData
  // are all added to state.
  state = {
    allRowsHighlighted: false,
    loading: false,
    highlightedRows: [],
    noResults: false,
    results: [],
    tableSort: {
      col: '',
      asc: false,
    },
    ...DBSets[this.props.dbSet],
  }

  componentDidMount () {
    // check to see if the form should be submitted once it mounts.
    // this is for DB Search forms where previous form values persist
    const {
      formName,
      triggerSearch,
      triggerSearchOnMount,
    } = this.props

    if (triggerSearchOnMount) {
      triggerSearch(formName)
    }
  }

  componentWillReceiveProps (np, ns) {
    // clear out the results when the user dirties the form.
    // users asked that this happen so you can't have a search
    // form which doesn't reflect the results below.
    if (np.formIsPristine === false && np.formIsPristine !== this.props.formIsPristine) {
      this.setState(() => ({
        results: [],
      }))
    }
  }

  // sorting of DataTable's rows
  handleSort = async newCol => {
    await this.setState(() => ({ loading: true }))

    const {
      results,
      sortData,
      tableSort: { col, asc },
    } = this.state

    const tableSort = {
      col: newCol,
      asc: col !== newCol ? true : !asc,
    }

    await sortData(results, tableSort, async (err, sortedData) => {
      if (err) {
        console.warn(err)
      }
      await this.setState((state, props) => ({
        loading: false,
        tableSort,
        results: sortedData,
      }))
    })
  }

  // search API for matches
  handleSubmit = async (formValues, dispatch, formProps) => {
    // adds layer of validation for submit
    if (preventFormSubmission(formProps)) {
      return
    }

    await this.setState(() => ({
      loading: true,
      noResults: false,
      results: [],
    }))

    const {
      fetchData,
      getSortColByFormValues,
      sortData,
      prepData,
    } = this.state

    const {
      dbSet,
      queryBasis,
      includeFlags,
      paginate,
      submittedFormIsPristine,
      unitType,
    } = this.props

    // based on form's values, determine which column to sort by.
    const col = getSortColByFormValues(formValues)

    const tableSort = {
      col,
      asc: true,
    }

    let results

    try {
      const fetchConfig = {
        dispatch,
        formProps,
        formValues,
        includeFlags,
        queryBasis,
        submittedFormIsPristine,
      }
      const resp = await fetchData(fetchConfig)
      const {
        count,
        entities,
        page,
        totalPages,
      } = resp

      // assign the returned entites to results
      results = entities

      // the browser cannot gracefully respond when maximum call stack is exceeded
      // this attempts to prevent this from happening during sortData.
      // the API should prevent more than 2k results but just in case,
      // check it here too
      if (results.length > 2000) {
        throw new Error(TOO_MANY_RESULTS)
      }

      const initializeFormValues = {
        ...formValues,
      }

      // for pagination, we need to treat the current form values as the initialized state
      // see ../common/Pagination for more information
      if (paginate) {
        initializeFormValues.pagination = {
          count,
          page,
          totalPages,
        }
      }

      // pathDb, pathDatabaseImport and afsIgnorePaths should be collapsed after a submission if there are results
      if (['pathDatabaseImport', 'afsIgnorePaths'].includes(dbSet) && results.length) {
        initializeFormValues.advancedSearch = []
      }

      // reinitialize the form with the current values so it's considered pristine
      // this allows for the pagination component to display
      // and ensures that triggerSearch will handle properly on return
      formProps.initialize(initializeFormValues)

      // leave the rest of this inside of the try. that way if there's an issue with
      // invalid data or parsing, we can gracefully tell the user what's happening.
      const preppedResults = await prepData(results, unitType, formValues)
      await sortData(preppedResults, tableSort, async (err, sortedData) => {
        if (err) {
          console.warn(err)
        }

        await this.setState(() => ({
          allRowsHighlighted: false,
          highlightedRows: [],
          loading: false,
          noResults: !sortedData.length,
          results: sortedData,
          tableSort,
        }))
      })
    } catch (err) {
      console.warn('SearchAndSelect error: ', err)
      this.setState(() => ({
        allRowsHighlighted: false,
        highlightedRows: [],
        loading: false,
        results: [],
      }))

      // errors may be from formatting invalid data,
      // from API response or uncaught. this ensures whatever the case,
      // there will be a message in the UI speaking to what happened.
      throw new SubmissionError({ _error: err.message || UNCAUGHT_ERROR })
    }
  }

  // trigger select row handler
  rowClick = d => {
    const {
      closeModal,
      dbSet,
      selectRow,
    } = this.props

    selectRow(d)

    // if we're in a lookup, close the modal after is applied selection
    // but only if it's not a path import lookup.
    // path import modal has a success messaging to display first.
    if (closeModal && dbSet !== 'pathDatabaseImport' && dbSet !== 'projectPathImport') {
      closeModal()
    }
  }

  // export search results as CSV
  // if you need to scrub the data before creating the CSV
  // see comments in create2dArrayOfResults, pass the 'scrubber'
  // to this method. The scrubber should NOT transform the actual results state.
  // it should be a reducer which creates new array of new objects for each in results.
  // for an example of an implemented scrubber, see ./Sets/RadioDb/scrubForCSV
  exportResults = async scrubber => {
    const {
      dbSet,
      warning,
    } = this.props

    await parseAndExportResultsData(this.state, this.props, scrubber)
    if (dbSet === 'radioDb' || dbSet === 'antennaDb') {
      warning({
        autoDismiss: 6,
        children: (
          <p id={'search-and-select-export-results-warning'}>
            {'Note: this will only export the visible page of search results. Navigate to another page to export those contents'}
          </p>
        ),
      })
    }
  }

  // uniqueValue will be rowData[uniqueId]
  rowHighlightToggle = uniqueValue => {
    const { highlightedRows } = this.state

    // only toggle highlight if we're supposed to
    if (!this.props.highlightRows) {
      return
    }

    const indexOfHighlightedRow = highlightedRows.indexOf(uniqueValue)
    const newHighlightedRows = [...highlightedRows]

    if (~indexOfHighlightedRow) {
      newHighlightedRows.splice(indexOfHighlightedRow, 1)
    } else {
      newHighlightedRows.push(uniqueValue)
    }

    this.setState(() => ({
      allRowsHighlighted: false,
      highlightedRows: newHighlightedRows,
    }))
  }

  // toggles highlight on all results
  allRowsHightlightToggle = e => {
    e.stopPropagation()

    const {
      allRowsHighlighted,
      results,
    } = this.state

    if (!results.length) {
      return
    }

    const highlightedRows = []

    if (!allRowsHighlighted) {
      results.forEach(d => {
        highlightedRows.push(d[d.uniqueId])
      })
    }

    this.setState(() => ({
      allRowsHighlighted: !allRowsHighlighted,
      highlightedRows,
    }))
  }

  render () {
    const {
      allRowsHightlightToggle,
      exportResults,
      handleSort,
      props,
      rowClick,
      rowHighlightToggle,
      state,
      toggleAdvanced,
    } = this

    const {
      allRowsHighlighted,
      columns,
      loading,
      Form,
      highlightedRows,
      Menu,
      noResults,
      results,
      tableSort,
    } = state

    const {
      closeModal,
      customImportHandler,
      dbSet,
      dynamicColumns,
      formName,
      highlightRows,
      idRoot,
      initialValues,
      label,
      renderOpenInNewTab,
      rowClass,
      shouldRowUpdate,
      triggerSearch,
      unitType,
    } = props

    // this should never be seen in production.
    // but gracefully handle when an unmapped set has been configured.
    if (!Form) {
      return (
        <div className={'error-message'}>
          {`Warning: unmapped <SearchAndSelect /> set of ${dbSet} assigned.`}
        </div>
      )
    }

    const loadingIcon = (
      loading
        ? (
          <div className={'loading-icon'}>
            <i className={'fa fa-spinner fa-spin'} />
          </div>
        ) : null
    )

    const hasResults = !!results.length

    const tableColumns = dynamicColumns || columns

    return (
      <div
        className={'database-search-wrapper bg-blue-dark'}
        id={`${idRoot}-modal`}>
        <Header
          allRowsHighlighted={allRowsHighlighted}
          allRowsHightlightToggle={allRowsHightlightToggle}
          closeModal={closeModal}
          hasResults={hasResults}
          highlightRows={highlightRows}
          highlightedRows={highlightedRows}
          idRoot={idRoot}
          label={label}
          Menu={Menu}
          results={results}
          sourceForm={formName}
          triggerSearch={triggerSearch} />
        <Form
          customImportHandler={customImportHandler}
          exportResults={exportResults}
          form={formName}
          hasResults={hasResults}
          highlightRows={highlightRows}
          highlightedRows={highlightedRows}
          initialValues={initialValues}
          onSubmit={this.handleSubmit}
          toggleAdvanced={toggleAdvanced} />
        <DataTable
          className={`search-and-select-${dbSet}`}
          columns={tableColumns}
          customProps={{
            highlightRows,
            highlightedRows,
            renderOpenInNewTab,
            rowHighlightToggle,
            shouldRowUpdate,
          }}
          data={results}
          noResults={noResults}
          idRoot={`${idRoot}-modal-table`}
          rowClick={rowClick}
          rowClass={rowClass}
          handleSort={handleSort}
          tableSort={tableSort}
          unitType={unitType} />
        {loadingIcon}
      </div>
    )
  }
}

const mapStateToProps = ({ form }, { formName }) => ({
  formIsPristine: isPristine(formName)({ form }),
})

// these are being bound to this component's props
// and will be used in initializeDbSearch
const mapDispatchToProps = dispatch => (
  bindActionCreators({
    submittedFormIsPristine,
    triggerSearch,
    warning,
  }, dispatch)
)

const ConnectedMappingResults = connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { forwardRef: true },
)(SearchAndSelect)

const RefSearchAndSelect = forwardRef((props, ref) => (
  <ConnectedMappingResults
    {...props}
    ref={ref} />
))

RefSearchAndSelect.displayName = 'SearchAndSelect'

export default RefSearchAndSelect